if (!customElements.get('user-alliance-tabs')) {
  class UserAllianceTabs extends HTMLElement {
    connectedCallback() {
      this.onAuthChange = () => {
        const session = window.UserAllianceAuth?.getSession?.() || {};
        this.hidden = Boolean(session.accessToken && session.email);
        if (this.hidden) this.stopCarousel?.();
        else if (this.panels) this.startCarousel(this.panels.find((panel) => !panel.hidden) || this.panels[0]);
      };
      document.addEventListener('user-alliance:auth-change', this.onAuthChange);
      this.onAuthChange();
      this.tabs = Array.from(this.querySelectorAll('[data-ua-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-ua-panel]'));
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      this.autoplay = this.dataset.autoplay !== 'false';
      this.interval = Number(this.dataset.interval) || 4000;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onTabClick = (event) => this.activate(Number(event.currentTarget.dataset.uaTab));
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onBlockSelect = (event) => this.handleBlockSelect(event);
      this.onDotClick = (event) => this.handleDotClick(event);
      this.onArrowClick = (event) => this.handleArrowClick(event);
      this.onMediaEnter = () => this.pauseCarousel();
      this.onMediaLeave = () => this.resumeCarousel();

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onKeydown);
      });
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.querySelectorAll('[data-ua-dot]').forEach((dot) => {
        dot.addEventListener('click', this.onDotClick);
      });
      this.querySelectorAll('[data-ua-previous], [data-ua-next]').forEach((arrow) => {
        arrow.addEventListener('click', this.onArrowClick);
      });
      this.querySelectorAll('[data-ua-carousel]').forEach((media) => {
        media.addEventListener('mouseenter', this.onMediaEnter);
        media.addEventListener('mouseleave', this.onMediaLeave);
      });

      if (!this.hidden) this.startCarousel(this.panels.find((panel) => !panel.hidden) || this.panels[0]);
    }

    disconnectedCallback() {
      document.removeEventListener('user-alliance:auth-change', this.onAuthChange);
      this.stopCarousel();
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onKeydown);
      });
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.querySelectorAll('[data-ua-dot]').forEach((dot) => {
        dot.removeEventListener('click', this.onDotClick);
      });
      this.querySelectorAll('[data-ua-previous], [data-ua-next]').forEach((arrow) => {
        arrow.removeEventListener('click', this.onArrowClick);
      });
      this.querySelectorAll('[data-ua-carousel]').forEach((media) => {
        media.removeEventListener('mouseenter', this.onMediaEnter);
        media.removeEventListener('mouseleave', this.onMediaLeave);
      });
    }

    activate(index, focus) {
      if (!Number.isInteger(index) || index < 0 || index >= this.tabs.length) return;

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      if (focus) this.tabs[index].focus();
      this.startCarousel(this.panels[index]);
    }

    startCarousel(panel) {
      this.stopCarousel();
      if (!panel) return;

      const root = panel.querySelector('[data-ua-carousel]');
      if (!root) return;

      this.carouselRoot = root;
      this.carouselSlides = Array.from(root.querySelectorAll('[data-ua-slide]'));
      this.carouselDots = Array.from(root.querySelectorAll('[data-ua-dot]'));
      if (this.carouselSlides.length < 2) return;

      const activeIndex = this.carouselSlides.findIndex((slide) => slide.classList.contains('is-active'));
      this.carouselIndex = activeIndex >= 0 ? activeIndex : 0;
      this.showSlide(this.carouselIndex);
      this.resumeCarousel();
    }

    stopCarousel() {
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
        this.carouselTimer = null;
      }
      this.carouselPaused = true;
    }

    resumeCarousel() {
      if (!this.autoplay || this.reduceMotion || !this.carouselSlides || this.carouselSlides.length < 2) return;
      this.carouselPaused = false;
      if (this.carouselTimer) clearInterval(this.carouselTimer);
      this.carouselTimer = setInterval(() => {
        if (this.carouselPaused) return;
        this.showSlide((this.carouselIndex + 1) % this.carouselSlides.length);
      }, this.interval);
    }

    pauseCarousel() {
      this.carouselPaused = true;
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
        this.carouselTimer = null;
      }
    }

    showSlide(index) {
      this.carouselIndex = index;
      this.carouselSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      this.carouselDots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    handleDotClick(event) {
      event.preventDefault();
      const index = Number(event.currentTarget.dataset.uaDot);
      if (!Number.isInteger(index)) return;
      const panel = event.currentTarget.closest('[data-ua-panel]');
      if (panel && this.carouselRoot !== panel.querySelector('[data-ua-carousel]')) {
        this.startCarousel(panel);
      }
      this.showSlide(index);
      this.resumeCarousel();
    }

    handleArrowClick(event) {
      event.preventDefault();
      const root = event.currentTarget.closest('[data-ua-carousel]');
      if (!root) return;

      if (this.carouselRoot !== root) {
        this.startCarousel(root.closest('[data-ua-panel]'));
      }

      const direction = event.currentTarget.hasAttribute('data-ua-previous') ? -1 : 1;
      const nextIndex = (this.carouselIndex + direction + this.carouselSlides.length) % this.carouselSlides.length;
      this.showSlide(nextIndex);
      this.resumeCarousel();
    }

    handleKeydown(event) {
      const last = this.tabs.length - 1;
      const index = Number(event.currentTarget.dataset.uaTab);
      let nextIndex = index;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index === 0 ? last : index - 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = last;
      else return;

      event.preventDefault();
      this.activate(nextIndex, true);
    }

    handleBlockSelect(event) {
      const panel = event.target.closest('[data-ua-panel]');
      const tab = event.target.closest('[data-ua-tab]');
      if (panel) this.activate(Number(panel.dataset.uaPanel));
      else if (tab) this.activate(Number(tab.dataset.uaTab));
    }
  }

  customElements.define('user-alliance-tabs', UserAllianceTabs);
}
